// Intake.AI - Agreement Page
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  ChevronLeft,
  FileText,
  Send,
  Loader2,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AgreementPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [lead, setLead] = useState(null);
  const [agreementText, setAgreementText] = useState('');
  const [feeArrangement, setFeeArrangement] = useState('contingency');
  const [retainerAmount, setRetainerAmount] = useState('');
  const [firmName, setFirmName] = useState('');
  const [attorneyName, setAttorneyName] = useState('');
  const [agreementId, setAgreementId] = useState(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    fetchLead();
  }, [leadId, token]);

  const fetchLead = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/leads/${leadId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setLead(await res.json());
      } else {
        toast.error('Lead not found');
        navigate('/intake');
      }
    } catch (error) {
      toast.error('Failed to load lead');
    } finally {
      setLoading(false);
    }
  };

  const generateAgreement = async () => {
    if (!firmName || !attorneyName) {
      toast.error('Please enter firm name and attorney name');
      return;
    }
    
    setGenerating(true);
    try {
      const formData = new FormData();
      formData.append('fee_arrangement', feeArrangement);
      formData.append('firm_name', firmName);
      formData.append('attorney_name', attorneyName);
      if (retainerAmount) formData.append('retainer_amount', retainerAmount);
      
      const res = await fetch(`${API_URL}/api/intake/leads/${leadId}/agreements/generate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        const data = await res.json();
        setAgreementText(data.agreement_text);
        toast.success('Agreement generated');
      } else {
        toast.error('Failed to generate agreement');
      }
    } catch (error) {
      toast.error('Failed to generate agreement');
    } finally {
      setGenerating(false);
    }
  };

  const sendAgreement = async () => {
    if (!agreementText) {
      toast.error('Please generate an agreement first');
      return;
    }
    
    setSending(true);
    try {
      // Create agreement
      const createRes = await fetch(`${API_URL}/api/intake/leads/${leadId}/agreements`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_id: leadId,
          agreement_type: 'legal_services',
          terms_content: agreementText,
          fee_arrangement: feeArrangement,
          retainer_amount: retainerAmount ? parseFloat(retainerAmount) : null
        })
      });
      
      if (!createRes.ok) {
        toast.error('Failed to create agreement');
        return;
      }
      
      const agreement = await createRes.json();
      setAgreementId(agreement.id);
      
      // Send agreement
      const sendRes = await fetch(`${API_URL}/api/intake/agreements/${agreement.id}/send`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (sendRes.ok) {
        setSent(true);
        toast.success('Agreement sent successfully!');
      } else {
        const error = await sendRes.json();
        toast.error(error.detail || 'Failed to send agreement');
      }
    } catch (error) {
      toast.error('Failed to send agreement');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-center">
          <FileText className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold mb-2">Agreement Sent!</h2>
            <p className="text-muted-foreground mb-6">
              The legal services agreement has been sent to {lead?.contact?.email || 'the client'}.
              They will receive a link to review and sign the agreement.
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => navigate(`/intake/leads/${leadId}`)}>
                Back to Lead
              </Button>
              <Button onClick={() => navigate('/intake')} className="bg-emerald-600 hover:bg-emerald-700">
                Go to Pipeline
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="agreement-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="flex h-16 items-center px-4 lg:px-8">
          <Link to={`/intake/leads/${leadId}`} className="flex items-center gap-2 mr-8">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Lead
            </Button>
          </Link>
          
          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold">Send Legal Services Agreement</h1>
            <p className="text-sm text-muted-foreground">
              For: {lead?.contact?.full_name || 'Unknown'}
            </p>
          </div>
        </div>
      </header>

      <div className="p-4 lg:p-8 max-w-4xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left - Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Agreement Details</CardTitle>
              <CardDescription>Configure the agreement terms</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Firm Name *</Label>
                <Input
                  value={firmName}
                  onChange={(e) => setFirmName(e.target.value)}
                  placeholder="Smith & Associates"
                />
              </div>
              <div>
                <Label>Attorney Name *</Label>
                <Input
                  value={attorneyName}
                  onChange={(e) => setAttorneyName(e.target.value)}
                  placeholder="John Smith, Esq."
                />
              </div>
              <div>
                <Label>Fee Arrangement</Label>
                <Select value={feeArrangement} onValueChange={setFeeArrangement}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contingency">Contingency Fee</SelectItem>
                    <SelectItem value="hourly">Hourly Rate</SelectItem>
                    <SelectItem value="flat_fee">Flat Fee</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(feeArrangement === 'hourly' || feeArrangement === 'flat_fee') && (
                <div>
                  <Label>Retainer Amount ($)</Label>
                  <Input
                    type="number"
                    value={retainerAmount}
                    onChange={(e) => setRetainerAmount(e.target.value)}
                    placeholder="5000"
                  />
                </div>
              )}
              <Button 
                className="w-full" 
                onClick={generateAgreement}
                disabled={generating || !firmName || !attorneyName}
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="w-4 h-4 mr-2" />
                    Generate Agreement
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Right - Preview */}
          <div className="lg:col-span-2">
            <Card className="h-full">
              <CardHeader>
                <CardTitle className="text-lg">Agreement Preview</CardTitle>
                <CardDescription>Review and edit before sending</CardDescription>
              </CardHeader>
              <CardContent>
                {agreementText ? (
                  <>
                    <Textarea
                      value={agreementText}
                      onChange={(e) => setAgreementText(e.target.value)}
                      className="min-h-[500px] font-mono text-sm"
                    />
                    <div className="mt-4 flex justify-end">
                      <Button 
                        onClick={sendAgreement}
                        disabled={sending}
                        className="bg-emerald-600 hover:bg-emerald-700"
                      >
                        {sending ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4 mr-2" />
                            Send to Client
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="h-[500px] flex items-center justify-center text-center text-muted-foreground">
                    <div>
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p>Generate an agreement to preview it here</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
